//====== WR data 15/09/2026 — SmartFlow Item Importer (Fase 3)
// Motivo da alteração: lógica PURA da tela de Item — mesmo espírito de importerLogic.js
// (Parceiro): formatação, montagem e validação do payload SAP, sem dependência de UI/Mongo. O
// usuário registrou intenção de reaproveitar esta rotina no Portal LiveModa mais adiante — por
// isso este arquivo não importa nada de reactstrap/services/api, só o normalizarTexto puro de
// importerLogic.js (também sem essas dependências).
//======
import { normalizarTexto } from "components/SmartFlow/importerLogic"

export function formatQuantidade(value) {
  if (value === null || value === undefined) return "—"
  return Number(value).toLocaleString("pt-BR", { minimumFractionDigits: 0, maximumFractionDigits: 4 })
}

export function formatValorItem(value) {
  if (value === null || value === undefined) return "—"
  return Number(value).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
}

export function formatConfianca(confianca) {
  return { alta: "Alta", media: "Média", baixa: "Baixa" }[confianca] || "—"
}

//====== Chave de deduplicação de um item bruto (extraído do XML), pra não listar o mesmo produto
// 2x quando ele aparece em notas de compra E de venda (pastas Fornecedores + Clientes
// consolidadas — decisão do usuário, plano seção "Consolidar pastas"). EAN exato primeiro; sem
// EAN, cai pra NCM + descrição normalizada (mesmo padrão de fallback que Parceiro usa pro CNPJ:
// documento -> idEstrangeiro -> arquivo).
export function chaveDedupItem(item) {
  if (item.cEAN) return `ean:${item.cEAN}`
  const ncm = item.ncm || "semncm"
  const desc = normalizarTexto(item.xProd || item.cProd || "")
  return `ncm-desc:${ncm}:${desc}`
}

//====== Monta 1 linha no formato SAP (Items) a partir do item bruto (XML) + a sugestão da IA
// (pode vir vazia — o usuário preenche na mão) + o ItemCode final (sequência ou reaproveitado).
// `sugestao` já normalizada pelo backend (SmartFlowItemAiService.normalizarSugestao). `ncmAbsEntry`
// vem de uma consulta ao vivo na Service Layer (buscarNcmsValidos, WR 16/09/2026) — é o AbsEntry
// (ID interno) do registro em NCMCodesSetup correspondente ao NCM do item, quando ele já existe na
// tabela oficial daquele tenant específico (undefined/null quando não existe). WR 16/09/2026 (v2):
// era um booleano `ncmValido` — corrigido pro valor de verdade que precisa ir no payload do Item
// (Items.NCMCode é chave estrangeira pro AbsEntry, não pro código do NCM em si).
export function montarItemSap(item, itemCode, sugestao, jaGravadoNoSap, usouSequencia, ncmAbsEntry) {
  const s = sugestao || {}
  return {
    // chave de deduplicação já calculada na listagem (Fase 3) — identificador ESTÁVEL da linha,
    // independente do ItemCode (que é editável e pode colidir se o usuário digitar igual em duas
    // linhas). Usado como key da grade de revisão em vez de fileId+ItemCode.
    chave: item.chave,
    fileId: item.fileId,
    numeroNF: item.numeroNF,
    fileName: item.fileName,
    jaGravadoNoSap: !!jaGravadoNoSap,
    usouSequencia: !!usouSequencia,
    // ItemCode original (sequência ou EAN já existente) — guardado pra poder VOLTAR a ele se o
    // usuário aceitar e depois desistir de tratar a linha como atualização de uma possível
    // duplicidade sugerida pela IA (ver "Usar este código"/"Manter novo" na grade).
    itemCodeOriginal: itemCode || null,

    // origem (referência, não editável na grade)
    cProd: item.cProd,
    xProd: item.xProd,
    cEAN: item.cEAN,
    ncm: item.ncm,
    cfop: item.cfop,
    unidadeComercial: item.unidadeComercial,
    quantidadeComercial: item.quantidadeComercial,
    valorUnitarioComercial: item.valorUnitarioComercial,
    valorTotalProduto: item.valorTotalProduto,
    grupoTributarioIcms: item.grupoTributarioIcms,
    grupoTributarioIpi: item.grupoTributarioIpi,
    grupoTributarioPis: item.grupoTributarioPis,
    grupoTributarioCofins: item.grupoTributarioCofins,
    //====== WR data 16/09/2026 — pedido do usuário: campos vistos na tela real do Cadastro do
    // Item do SAP que ainda não eram levados. origemIcms/temRastroLoteXml são dado EXATO do XML
    // (não editável — referência); os demais (MaterialType/AdministrarPor/CestCode/WTLiable) são
    // os campos finais, editáveis na grade.
    origemIcms: item.origemIcms,
    // ncmAbsEntry é o que de fato vai no payload do Item (ver montarPayloadItem em
    // itemSapWrite.js); ncmValido é só a flag derivada usada pra exibição (grade/Excel).
    ncmAbsEntry: ncmAbsEntry != null ? ncmAbsEntry : null,
    ncmValido: ncmAbsEntry != null,
    temRastroLoteXml: !!item.temRastroLoteXml,

    // sugestão da IA (informativa — mostrada na grade, nunca gravada sozinha)
    confiancaIA: s.confianca || null,
    possivelDuplicidadeItemCode: s.possivelDuplicidadeItemCode || null,
    observacaoIA: s.observacao || null,

    // campos finais — editáveis, o que de fato vai pro SAP
    ItemCode: itemCode || s.itemCode || item.cProd || "",
    ItemName: s.itemName || item.xProd || "",
    ItemsGroupCode: s.itemsGroupCode ?? null,
    InventoryUOM: s.inventoryUOM || item.unidadeComercial || "",
    InventoryItem: s.inventoryItem || "tYES",
    SalesItem: s.salesItem || "tYES",
    PurchaseItem: s.purchaseItem || "tYES",
    Valid: "tYES",
    // Tipo de material (SAPB1.BoMaterialTypes) — "Serviços" (mt_Service) é a mesma opção que
    // cobre o rádio "Serviço x Material" da tela do SAP, não existe campo separado pra isso.
    MaterialType: s.materialType || null,
    // Sem sugestão exata da IA, cai no que o XML já prova (rastro de lote) ou "nenhum" — nunca
    // "serie" por padrão (mudar isso depois de o item ter movimento no SAP não é trivial).
    AdministrarPor: s.administrarPor || (item.temRastroLoteXml ? "lote" : "nenhum"),
    // CEST é código de tabela oficial por NCM — a IA NUNCA sugere valor (chutar seria pior que
    // deixar em branco); fica sempre em branco até o usuário preencher, se souber.
    CestCode: "",
    // Sujeito a retenção de imposto — pedido do usuário: vem SEMPRE desmarcado pra item que não é
    // serviço (o padrão do SAP vinha marcado errado pra material).
    WTLiable: s.materialType === "mt_Service" ? "tYES" : "tNO",

    sugestaoIA: sugestao || null,
  }
}

//====== Simula as validações da Service Layer antes de deixar marcar/gravar uma linha. Campos
// obrigatórios confirmados na Fase 1 (plano seção 3.4) — sujeito a ajuste na Fase 4 (teste real).
export function validarItemSap(row, contagemItemCodeNoLote) {
  const erros = []
  const itemCode = (row.ItemCode || "").trim()
  const itemName = (row.ItemName || "").trim()

  if (!itemCode) {
    erros.push("ItemCode é obrigatório.")
  } else {
    if (itemCode.length > 10) erros.push("ItemCode excede 10 caracteres (limite comum do SAP nesse campo).")
    if ((contagemItemCodeNoLote.get(itemCode) || 0) > 1) {
      erros.push("ItemCode duplicado no lote — é chave primária, não pode repetir.")
    }
  }

  if (!itemName) {
    erros.push("ItemName é obrigatório.")
  } else if (itemName.length > 100) {
    erros.push("ItemName excede 100 caracteres (limite do SAP).")
  }

  if (!row.ItemsGroupCode) {
    erros.push("Grupo do Item é obrigatório.")
  }

  //====== WR data 05/10/2026 — Motivo da alteração: reportado ao vivo — itens com Tipo de material
  // vazio na grade (IA não sugeriu) iam pro SAP sem MaterialType no payload, e o SAP aplicava o
  // padrão dele (Matéria-prima) sem nenhum aviso. Agora é obrigatório escolher na grade.
  if (!row.MaterialType) {
    erros.push("Tipo de material é obrigatório.")
  }

  if (!["tYES", "tNO"].includes(row.InventoryItem)) erros.push("Estoque precisa ser tYES ou tNO.")
  if (!["tYES", "tNO"].includes(row.SalesItem)) erros.push("Venda precisa ser tYES ou tNO.")
  if (!["tYES", "tNO"].includes(row.PurchaseItem)) erros.push("Compra precisa ser tYES ou tNO.")

  return { ok: erros.length === 0, erros }
}
